import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { notificationsApi, NOTIFICATIONS_PAGE_SIZE } from '../services/api';
import type { AppNotification } from '../types/Notification';
import { RootState } from './store';

interface NotificationsState {
  items: AppNotification[];
  unreadCount: number;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  /** Separate from `status` so paging never blanks the list already on screen. */
  loadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  /** Realtime channel health, surfaced as a quiet hint in the dropdown. */
  connection: 'connecting' | 'connected' | 'disconnected';
  /**
   * Bumped whenever realtime decides app data is stale. Exists for data that
   * does not live in the store — the calendar loads bookings through its own
   * api call keyed on a refresh token, so refetching agents and conversations
   * does nothing for it.
   */
  dataRefreshToken: number;
}

const initialState: NotificationsState = {
  items: [],
  unreadCount: 0,
  status: 'idle',
  loadingMore: false,
  hasMore: true,
  error: null,
  connection: 'connecting',
  dataRefreshToken: 0,
};

const byNewestFirst = (a: AppNotification, b: AppNotification) =>
  new Date(b.created_at).getTime() - new Date(a.created_at).getTime();

/**
 * First page plus the unread count. Also used to catch up after the realtime
 * channel reconnects or the tab regains focus, so it must be safe to re-run at
 * any time — it replaces the head of the list rather than appending to it.
 */
export const fetchNotifications = createAsyncThunk(
  'notifications/fetchNotifications',
  async () => {
    const [items, unreadCount] = await Promise.all([
      notificationsApi.fetchNotifications(),
      notificationsApi.fetchUnreadCount(),
    ]);
    return { items, unreadCount };
  }
);

export const fetchMoreNotifications = createAsyncThunk(
  'notifications/fetchMoreNotifications',
  async (_, { getState }) => {
    const { items } = (getState() as RootState).notifications;
    const oldest = items[items.length - 1];
    return notificationsApi.fetchNotifications(oldest?.created_at);
  }
);

export const markNotificationsRead = createAsyncThunk(
  'notifications/markNotificationsRead',
  async (ids: number[] | undefined) => {
    const updatedIds = await notificationsApi.markRead(ids);
    return { updatedIds, readAt: new Date().toISOString() };
  }
);

export const clearNotifications = createAsyncThunk(
  'notifications/clearNotifications',
  async () => {
    await notificationsApi.clearAll();
  }
);

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    resetNotificationsState: () => initialState,

    /**
     * A row arriving over the realtime channel. Deduplicated by id because a
     * reconnect catch-up fetch and a live INSERT can deliver the same row.
     */
    notificationReceived: (state, action: PayloadAction<AppNotification>) => {
      const incoming = action.payload;
      if (state.items.some(item => item.id === incoming.id)) return;

      state.items.unshift(incoming);
      state.items.sort(byNewestFirst);
      if (!incoming.read_at) state.unreadCount += 1;
    },

    /** An UPDATE from the channel — typically this user marking read elsewhere. */
    notificationUpdated: (state, action: PayloadAction<AppNotification>) => {
      const incoming = action.payload;
      const index = state.items.findIndex(item => item.id === incoming.id);
      if (index === -1) return;

      const wasUnread = !state.items[index].read_at;
      state.items[index] = incoming;
      if (wasUnread && incoming.read_at) {
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },

    notificationRemoved: (state, action: PayloadAction<number>) => {
      const index = state.items.findIndex(item => item.id === action.payload);
      if (index === -1) return;
      if (!state.items[index].read_at) {
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
      state.items.splice(index, 1);
    },

    /** Tells token-driven fetchers (the calendar) to reload. */
    dataRefreshRequested: state => {
      state.dataRefreshToken += 1;
    },

    setRealtimeConnection: (
      state,
      action: PayloadAction<NotificationsState['connection']>
    ) => {
      state.connection = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchNotifications.pending, state => {
        state.status = 'loading';
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        const { items, unreadCount } = action.payload;
        state.status = 'succeeded';
        state.error = null;
        state.unreadCount = unreadCount;

        // Keep anything already paged in below the refreshed head, so a focus
        // refresh does not collapse a list the user has scrolled back through.
        const refreshedIds = new Set(items.map(item => item.id));
        const olderThanPage = state.items.filter(
          item =>
            !refreshedIds.has(item.id) &&
            items.length === NOTIFICATIONS_PAGE_SIZE &&
            new Date(item.created_at).getTime() <
              new Date(items[items.length - 1].created_at).getTime()
        );

        state.items = [...items, ...olderThanPage].sort(byNewestFirst);
        state.hasMore = items.length === NOTIFICATIONS_PAGE_SIZE;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || null;
      })

      .addCase(fetchMoreNotifications.pending, state => {
        state.loadingMore = true;
      })
      .addCase(
        fetchMoreNotifications.fulfilled,
        (state, action: PayloadAction<AppNotification[]>) => {
          state.loadingMore = false;
          const known = new Set(state.items.map(item => item.id));
          const fresh = action.payload.filter(item => !known.has(item.id));
          state.items = [...state.items, ...fresh].sort(byNewestFirst);
          state.hasMore = action.payload.length === NOTIFICATIONS_PAGE_SIZE;
        }
      )
      .addCase(fetchMoreNotifications.rejected, (state, action) => {
        state.loadingMore = false;
        state.error = action.error.message || null;
      })

      .addCase(markNotificationsRead.pending, (state, action) => {
        // Optimistic: the dot should clear the instant the bell opens.
        const ids = action.meta.arg;
        const readAt = new Date().toISOString();
        state.items.forEach(item => {
          if (item.read_at) return;
          if (ids && !ids.includes(item.id)) return;
          item.read_at = readAt;
        });
        state.unreadCount = state.items.filter(item => !item.read_at).length;
      })
      .addCase(markNotificationsRead.rejected, (state, action) => {
        // Roll the badge back to the truth we can still see locally.
        state.error = action.error.message || null;
      })

      .addCase(clearNotifications.pending, state => {
        state.items = [];
        state.unreadCount = 0;
        state.hasMore = false;
      })
      .addCase(clearNotifications.rejected, (state, action) => {
        state.error = action.error.message || null;
      });
  },
});

export const {
  resetNotificationsState,
  notificationReceived,
  notificationUpdated,
  notificationRemoved,
  dataRefreshRequested,
  setRealtimeConnection,
} = notificationsSlice.actions;

export const selectNotifications = (state: RootState) =>
  state.notifications.items;
export const selectUnreadNotificationCount = (state: RootState) =>
  state.notifications.unreadCount;
export const selectNotificationsStatus = (state: RootState) =>
  state.notifications.status;
export const selectNotificationsLoadingMore = (state: RootState) =>
  state.notifications.loadingMore;
export const selectNotificationsHasMore = (state: RootState) =>
  state.notifications.hasMore;
export const selectNotificationsError = (state: RootState) =>
  state.notifications.error;
export const selectNotificationsConnection = (state: RootState) =>
  state.notifications.connection;
export const selectDataRefreshToken = (state: RootState) =>
  state.notifications.dataRefreshToken;

export default notificationsSlice.reducer;
